export const UNIDADES_DOSIS = [
  { value: 'comprimido', label: 'Comprimido', plural: 'comprimidos' },
  { value: 'capsula', label: 'Cápsula', plural: 'cápsulas' },
  { value: 'ml', label: 'ml', plural: 'ml' },
  { value: 'gota', label: 'Gota', plural: 'gotas' },
  { value: 'sobre', label: 'Sobre', plural: 'sobres' },
  { value: 'inyeccion', label: 'Inyección', plural: 'inyecciones' },
  { value: 'unidad', label: 'Unidad', plural: 'unidades' }
];

export const FRECUENCIAS_TOMA = [
  { value: 0.5, label: 'cada 48 h', sub: '½×/día', intervaloHoras: 48 },
  { value: 1, label: '1×/día', sub: 'cada 24 h', intervaloHoras: 24 },
  { value: 2, label: '2×/día', sub: 'cada 12 h', intervaloHoras: 12 },
  { value: 3, label: '3×/día', sub: 'cada 8 h', intervaloHoras: 8 },
  { value: 4, label: '4×/día', sub: 'cada 6 h', intervaloHoras: 6 },
  { value: 6, label: '6×/día', sub: 'cada 4 h', intervaloHoras: 4 }
];

export const RECETAS_CANTIDAD_BASE = [1, 2, 3, 6, 12];

export const PLAN_MESES_PRESETS = [1, 3, 6, 12];

export function resolveUnidadDosis(value) {
  return UNIDADES_DOSIS.find((u) => u.value === value) || UNIDADES_DOSIS[0];
}

export function pluralizarUnidad(unidad, cantidad) {
  const meta = resolveUnidadDosis(unidad);
  const n = Number(cantidad);
  if (!Number.isFinite(n) || n === 1) return meta.label.toLowerCase();
  return meta.plural;
}

export function addDaysIso(isoDate, days) {
  const baseIso = normalizeFechaIso(isoDate);
  if (!baseIso || !Number.isFinite(days)) return null;
  const base = new Date(`${baseIso}T12:00:00`);
  if (Number.isNaN(base.getTime())) return null;
  base.setDate(base.getDate() + days);
  return base.toISOString().split('T')[0];
}

export function normalizeFechaIso(dateString) {
  if (!dateString) return null;
  try {
    const s = String(dateString).trim();
    if (!s) return null;
    if (s.includes('T')) return s.split('T')[0];
    if (s.includes(' ')) {
      const maybe = s.split(' ')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(maybe)) return maybe;
    }
    const iso = s.match(/(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const ar = s.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (ar) return `${ar[3]}-${ar[2]}-${ar[1]}`;
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
    return null;
  } catch (e) {
    return null;
  }
}

export function formatFechaCorta(isoDate) {
  const normalized = normalizeFechaIso(isoDate);
  if (!normalized) return '—';
  const [y, m, d] = normalized.split('-');
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
}

export function calcularDosificacion({
  dosisPorToma,
  tomasPorDia,
  unidadesPorReceta,
  cantidadRecetas = 1,
  fechaInicio = null
} = {}) {
  const dosis = Number(dosisPorToma);
  const tomas = Number(tomasPorDia);
  const unidades = Number(unidadesPorReceta);
  const recetas = Math.max(1, Number(cantidadRecetas) || 1);

  if (!Number.isFinite(dosis) || dosis <= 0) return null;
  if (!Number.isFinite(tomas) || tomas <= 0) return null;
  if (!Number.isFinite(unidades) || unidades <= 0) return null;

  const consumoDiario = dosis * tomas;
  const duracionRecetaDias = Math.floor(unidades / consumoDiario);
  if (duracionRecetaDias <= 0) return null;

  const duracionPlanDias = duracionRecetaDias * recetas;
  const semanasReceta = Math.round((duracionRecetaDias / 7) * 10) / 10;
  const semanasPlan = Math.round((duracionPlanDias / 7) * 10) / 10;
  const mesesSugeridos = Math.max(1, Math.min(24, Math.ceil(duracionPlanDias / 30)));
  const fechaFinReceta = fechaInicio ? addDaysIso(fechaInicio, duracionRecetaDias - 1) : null;
  const fechaFinPlan = fechaInicio ? addDaysIso(fechaInicio, duracionPlanDias - 1) : null;
  const coberturaMesPct = Math.min(100, Math.round((duracionRecetaDias / 30) * 100));

  return {
    consumoDiario,
    duracionRecetaDias,
    duracionPlanDias,
    semanasReceta,
    semanasPlan,
    mesesSugeridos,
    fechaFinReceta,
    fechaFinPlan,
    coberturaMesPct,
    unidadesRestantesUltimoDia: unidades - consumoDiario * duracionRecetaDias
  };
}

export function buildRecetasCantidadOptions(extraValues = []) {
  const values = new Set(RECETAS_CANTIDAD_BASE);
  (extraValues || []).forEach((raw) => {
    const n = Math.round(Number(raw));
    if (Number.isFinite(n) && n > 0 && n <= 120) values.add(n);
  });
  return Array.from(values).sort((a, b) => a - b).map((n) => ({
    value: String(n),
    label: n === 1 ? '1 receta' : `${n} recetas`
  }));
}

/** Calcula período + cantidad de recetas para cubrir N meses según la dosificación. */
export function calcularSugerenciaPlanMeses({
  dosisPorToma,
  unidadDosis,
  tomasPorDia,
  unidadesPorReceta,
  mesesObjetivo = 1,
  fechaInicio = null
} = {}) {
  const base = calcularDosificacion({
    dosisPorToma,
    tomasPorDia,
    unidadesPorReceta,
    cantidadRecetas: 1,
    fechaInicio
  });
  if (!base) return null;

  const meses = Math.max(1, Math.min(24, Number(mesesObjetivo) || 1));
  const diasObjetivo = meses * 30;
  const cantidadRecetas = Math.max(1, Math.ceil(diasObjetivo / base.duracionRecetaDias));
  const duracionPlanDias = cantidadRecetas * base.duracionRecetaDias;
  const mesesEfectivos = Math.max(1, Math.min(24, Math.ceil(duracionPlanDias / 30)));
  const coberturaObjetivoPct = Math.min(100, Math.round((duracionPlanDias / diasObjetivo) * 100));
  const planCalculo = calcularDosificacion({
    dosisPorToma,
    tomasPorDia,
    unidadesPorReceta,
    cantidadRecetas,
    fechaInicio
  });
  const posologia = buildPosologiaDesdeDosificacion({
    dosisPorToma,
    unidadDosis,
    tomasPorDia,
    unidadesPorReceta
  });

  return {
    mesesObjetivo: meses,
    mesesEfectivos,
    cantidadRecetas,
    duracionPlanDias,
    diasObjetivo,
    coberturaObjetivoPct,
    duracionRecetaDias: base.duracionRecetaDias,
    consumoDiario: base.consumoDiario,
    fechaFinPlan: planCalculo?.fechaFinPlan || null,
    posologia
  };
}

export function buildPosologiaDesdeDosificacion({
  dosisPorToma,
  unidadDosis,
  tomasPorDia,
  unidadesPorReceta
} = {}) {
  const dosis = Number(dosisPorToma);
  const tomas = Number(tomasPorDia);
  const unidades = Number(unidadesPorReceta);
  if (!Number.isFinite(dosis) || dosis <= 0 || !Number.isFinite(tomas) || tomas <= 0) return '';

  const unidadTxt = pluralizarUnidad(unidadDosis, dosis);
  const freq = FRECUENCIAS_TOMA.find((f) => f.value === tomas);
  const freqLabel = freq ? freq.sub : `${tomas} veces al día`;

  let base = `${dosis} ${unidadTxt}, ${freqLabel}`;
  if (Number.isFinite(unidades) && unidades > 0) {
    base += ` · ${unidades} unidades por receta`;
  }
  return base;
}

export function createDosificacionStateFromPedido(pedido = {}) {
  return {
    dosisPorToma: pedido.dosis_por_toma != null && pedido.dosis_por_toma !== ''
      ? String(pedido.dosis_por_toma)
      : '1',
    unidadDosis: pedido.unidad_dosis || 'comprimido',
    tomasPorDia: pedido.tomas_por_dia != null && pedido.tomas_por_dia !== ''
      ? String(pedido.tomas_por_dia)
      : '1',
    unidadesPorReceta: pedido.unidades_por_receta != null && pedido.unidades_por_receta !== ''
      ? String(pedido.unidades_por_receta)
      : ''
  };
}

export function serializeDosificacionForApi(dosificacion = {}, calculo = null) {
  const dosis = Number(dosificacion.dosisPorToma);
  const tomas = Number(dosificacion.tomasPorDia);
  const unidades = Number(dosificacion.unidadesPorReceta);
  const hasData = Number.isFinite(dosis) && dosis > 0
    && Number.isFinite(tomas) && tomas > 0
    && Number.isFinite(unidades) && unidades > 0;

  if (!hasData) {
    return {
      dosis_por_toma: null,
      unidad_dosis: null,
      tomas_por_dia: null,
      unidades_por_receta: null,
      consumo_diario_unidades: null,
      duracion_receta_dias: null,
      fecha_fin_receta: null
    };
  }

  return {
    dosis_por_toma: dosis,
    unidad_dosis: dosificacion.unidadDosis || 'comprimido',
    tomas_por_dia: tomas,
    unidades_por_receta: Math.round(unidades),
    consumo_diario_unidades: calculo?.consumoDiario ?? dosis * tomas,
    duracion_receta_dias: calculo?.duracionRecetaDias ?? null,
    fecha_fin_receta: calculo?.fechaFinReceta ?? null
  };
}

export function resolveFrecuenciaLabel(tomasPorDia) {
  const tomas = Number(tomasPorDia);
  if (!Number.isFinite(tomas) || tomas <= 0) return '';
  const freq = FRECUENCIAS_TOMA.find((f) => f.value === tomas);
  return freq ? freq.label : `${tomas}×/día`;
}

/** Plan de recetas derivado del pedido aprobado (dosificación guardada o fallback 30 días). */
export function resolvePlanDosificacionPedido(pedido = {}) {
  const fechaInicio = normalizeFechaIso(pedido.fecha_inicio || pedido.fecha_solicitud);
  const cantidadRecetas = Math.max(1, Number(pedido.cantidad_recetas) || 1);
  const dosisPorToma = pedido.dosis_por_toma;
  const tomasPorDia = pedido.tomas_por_dia;
  const unidadesPorReceta = pedido.unidades_por_receta;
  const unidadDosis = pedido.unidad_dosis || 'comprimido';
  const duracionStored = Number(pedido.duracion_receta_dias);
  const consumoStored = Number(pedido.consumo_diario_unidades);

  const calculo = calcularDosificacion({
    dosisPorToma,
    tomasPorDia,
    unidadesPorReceta,
    cantidadRecetas,
    fechaInicio
  });

  const duracionRecetaDias = calculo?.duracionRecetaDias
    ?? (Number.isFinite(duracionStored) && duracionStored > 0 ? Math.round(duracionStored) : null);
  const intervaloDias = duracionRecetaDias || 30;
  const consumoDiario = calculo?.consumoDiario
    ?? (Number.isFinite(consumoStored) && consumoStored > 0 ? consumoStored : null);

  const hasPlan = Boolean(
    calculo
    || (Number.isFinite(duracionStored) && duracionStored > 0)
    || (
      Number(dosisPorToma) > 0
      && Number(tomasPorDia) > 0
      && Number(unidadesPorReceta) > 0
    )
  );

  const slots = [];
  for (let i = 1; i <= cantidadRecetas; i += 1) {
    const offset = (i - 1) * intervaloDias;
    const fechaInicioReceta = fechaInicio ? addDaysIso(fechaInicio, offset) : '';
    const fechaFinReceta = fechaInicio
      ? addDaysIso(fechaInicio, offset + intervaloDias - 1)
      : '';
    slots.push({
      numero: i,
      fechaPrevista: fechaInicioReceta,
      fechaInicio: fechaInicioReceta,
      fechaFin: fechaFinReceta,
      duracionDias: intervaloDias,
      unidades: Number(unidadesPorReceta) || null
    });
  }

  return {
    hasPlan,
    calculo,
    duracionRecetaDias,
    intervaloDias,
    consumoDiario,
    dosisPorToma: Number(dosisPorToma) || null,
    unidadDosis,
    tomasPorDia: Number(tomasPorDia) || null,
    unidadesPorReceta: Number(unidadesPorReceta) || null,
    posologia: pedido.posologia || '',
    fechaInicio,
    cantidadRecetas,
    slots,
    frecuenciaLabel: resolveFrecuenciaLabel(tomasPorDia)
  };
}

export function resolveVentanaEmisionReceta(fechaPrevista, {
  diasPasado = 5,
  diasFuturo = 5
} = {}) {
  const fecha = normalizeFechaIso(fechaPrevista);
  if (!fecha) return null;

  const desde = addDaysIso(fecha, -diasFuturo);
  const hasta = addDaysIso(fecha, diasPasado);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const hoyIso = hoy.toISOString().split('T')[0];

  const habilitadaHoy = Boolean(desde && hasta && hoyIso >= desde && hoyIso <= hasta);
  let estado = 'vencida';
  let estadoLabel = 'Fuera de ventana';
  if (habilitadaHoy) {
    estado = 'activa';
    estadoLabel = 'Disponible hoy';
  } else if (desde && hoyIso < desde) {
    estado = 'futura';
    estadoLabel = `Se habilita el ${formatFechaCorta(desde)}`;
  } else if (hasta) {
    estado = 'vencida';
    estadoLabel = `Ventana cerrada el ${formatFechaCorta(hasta)}`;
  }

  return {
    fechaPrevista: fecha,
    desde,
    hasta,
    habilitadaHoy,
    estado,
    estadoLabel,
    diasPasado,
    diasFuturo
  };
}

export function resolveRecetaPlanSlot(plan, numeroReceta, receta = null) {
  const num = Number(numeroReceta);
  const fromPlan = plan?.slots?.length
    ? (plan.slots.find((s) => s.numero === num) || plan.slots[num - 1] || null)
    : null;

  const fechaInicio = normalizeFechaIso(
    receta?.fecha_inicio_cobertura || receta?.fecha_prevista || fromPlan?.fechaInicio
  );
  const duracion = Number(receta?.duracion_dias || fromPlan?.duracionDias) || null;
  const fechaFin = normalizeFechaIso(
    receta?.fecha_fin_cobertura
    || fromPlan?.fechaFin
    || (fechaInicio && duracion ? addDaysIso(fechaInicio, duracion - 1) : null)
  );

  if (!fromPlan && !fechaInicio) return null;

  return {
    numero: num,
    fechaPrevista: fechaInicio || fromPlan?.fechaPrevista || '',
    fechaInicio: fechaInicio || fromPlan?.fechaInicio || '',
    fechaFin: fechaFin || fromPlan?.fechaFin || '',
    duracionDias: duracion || fromPlan?.duracionDias || null,
    unidades: Number(receta?.unidades_receta || fromPlan?.unidades) || null
  };
}
